import { CalendarIcon, IdentificationIcon, KeyIcon, MailIcon, PhoneIcon, UsersIcon } from '@heroicons/react/outline';
import FormInputField from '../../common/components/formElements/FormInputField';
import FullRow from '../../common/components/formElements/FullRow';
import HalfRow from '../../common/components/formElements/HalfRow';
import useInitialForm from './hooks/useInitialForm';
import { Controller } from 'react-hook-form';
import FormComboBox from '../../common/components/formElements/FormComboBox';
import ProfileFormHeader from '../../doctorUser/components/ProfileFormHeader/ProfileFormHeader';
import CustomRedAlert from '../../common/components/customRedAlert/CustomRedAlert';
import CustomSuccessModal from '../../common/components/customSuccessModal/CustomSuccessModal';
import usePasswordForm from './hooks/usePasswordForm';
import { CxCard, CxWrap, Line } from '../../v2/components/Sx';
import DatePicker from 'react-datepicker';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Country, State } from 'country-state-city';

const PersonalProfileForm = ({ userProfile, userId, userType }) => {
    const [diseases, setDiseases] = useState([{ name: '' }]);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [diseaseErrors, setDiseaseErrors] = useState(['']);
    const [countryOptions, setCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState('');

    const API_SERVER = process.env.REACT_APP_API_SERVER;


    const addDisease = () => {
        if (diseases.length < 5) {
            setDiseases([...diseases, { name: '' }]);
            setDiseaseErrors([...diseaseErrors, '']);
        }
    };
    useEffect(() => {
        if (userProfile?.diseases?.length) {
            setDiseases(userProfile.diseases);
            setDiseaseErrors(new Array(userProfile.diseases.length).fill(''));
        } else {
            setDiseases([{ name: '' }]);
            setDiseaseErrors(['']);
        }
    }, [userProfile]);

    const removeDisease = (index) => {
        const newDiseases = [...diseases];
        const newErrors = [...diseaseErrors];
        newDiseases.splice(index, 1);
        newErrors.splice(index, 1);
        setDiseases(newDiseases);
        setDiseaseErrors(newErrors);
    };

    const getDocumentTypes = async () => {
        await axios
            .get(`${API_SERVER}/admin-setup/document-types`)
            .then((res) => {
                setDocumentTypes(res?.data);
            })
            .catch((error) => {
                console.error(error);
            });
    };
    useEffect(() => {
        getDocumentTypes();
    }, []);

    const {
        formattedCountryData,
        handleSubmit,
        errors,
        register,
        setValue,
        control,
        saveProfileForm,
        personalDocuments,
        showErrorModal,
        showSuccessModal,
        onCloseSuccessMessage,
        onCloseErrorMessage,
        handleImageChange,
        previewImage,
        watch,
        getValues,
        trigger
    } = useInitialForm(userType, userProfile, userId, diseases, setDiseases, setDiseaseErrors);

    const {
        register: registerPassword,
        handleSubmit: handlePasswordSubmit,
        errors: passwordErrors,
        validatePasswordConfirmation,
        showErrorModal: showErrorModalPassword,
        showSuccessModal: showSuccessModalPassword,
        onUpdatePassword,
        onCloseErrorModal: onCloseErrorModalPassword,
        onCloseSuccessModal: onCloseSuccessModalPassword
    } = usePasswordForm(userType);

    const showError = showErrorModal || showErrorModalPassword;
    const showSuccess = showSuccessModal || showSuccessModalPassword;
    const errorMsg = showErrorModal
        ? 'No se pudo obtener información válida para guardar. Inténtelo de nuevo.'
        : 'No se pudo cambiar de contraseña. revise la información ingresada e inténtelo de nuevo';
    const successMsg = showSuccessModal ? 'Perfil actualizado con éxito' : 'Contraseña actualizada con éxito';

    const closeSuccess = () => {
        showSuccessModal ? onCloseSuccessMessage() : onCloseSuccessModalPassword();
    };

    const closeError = () => {
        showErrorModal ? onCloseErrorMessage() : onCloseErrorModalPassword();
    };
    const ErrMsg = ({ message }) => (message ? <p className="mt-2 text-sm text-red-700">{message}</p> : null);
    const handleDiseaseChange = (e, index) => {
        const { value } = e.target;
        const newDiseases = [...diseases];
        const newErrors = [...diseaseErrors];
        newDiseases[index].name = value;
        newErrors[index] = value.trim() === '' ? 'Este campo no puede estar vacío' : '';
        setDiseases(newDiseases);
        setDiseaseErrors(newErrors);
    };

    useEffect(() => {
        if (userProfile?.documentType && documentTypes.length) {
            setValue('documentType', userProfile.documentType);
        }
    }, [userProfile?.documentType, documentTypes, setValue]);

    useEffect(() => {
        if (userProfile?.country) {
            setValue('country', userProfile.country);
            setSelectedCountry(userProfile.country)
        }
        if (userProfile?.state) {
            setValue('state', userProfile.state);
        }
    }, [userProfile?.country, userProfile?.state, setValue]);

    useEffect(() => {
        const countries = Country.getAllCountries().map((c) => ({
            label: c.name,
            value: c.isoCode,
        }));

        setCountryOptions(countries);
    }, []);

    useEffect(() => {
        if (selectedCountry) {
            const states = State.getStatesOfCountry(selectedCountry).map((s) => ({
                label: s.name,
                value: s.isoCode,
            }));
            setStateOptions(states);

            // Optional: Reset state if current value is no longer valid
            if (!states.some((s) => s.value === userProfile?.state)) {
                setValue('state', '');
            }
        } else {
            setStateOptions([]);
        }
    }, [selectedCountry, setValue, userProfile?.state]);

    // Initialize phone fields from userProfile (only if form values are not already set)
    useEffect(() => {
        if (userProfile) {
            const countryCode = userProfile.countryCode || '';
            const phone = userProfile.phone || '';
            const currentFormPhone = getValues('phone');
            const currentFormCountryCode = getValues('countryCode');
            const currentFormFullPhone = getValues('fullPhone');
            
            // Check if we need to initialize phone values
            const needsInitialization = (countryCode || phone) && (!currentFormPhone || !currentFormCountryCode || !currentFormFullPhone);
            
            if (needsInitialization) {
                // Set countryCode if it exists in userProfile and is not set in form
                if (countryCode && !currentFormCountryCode) {
                    setValue('countryCode', countryCode, { shouldValidate: false });
                }
                
                // Set phone if it exists in userProfile and is not set in form
                if (phone && !currentFormPhone) {
                    setValue('phone', phone, { shouldValidate: false });
                }
                
                // Set fullPhone - prefer userProfile.fullPhone, otherwise compute from countryCode + phone
                if (!currentFormFullPhone || currentFormFullPhone.trim() === '') {
                    let fullPhone = userProfile.fullPhone;
                    if (!fullPhone && (countryCode || phone)) {
                        const cleanCode = (countryCode || '').replace(/^\+/, '');
                        fullPhone = `${cleanCode}${phone || ''}`;
                    }
                    if (fullPhone && fullPhone.trim() !== '') {
                        setValue('fullPhone', fullPhone, { shouldValidate: false });
                    }
                }
            }
        }
    }, [userProfile, setValue, getValues]);


    return (
        <>
            <div className="">
                <div className="profile-top-content">
                    <h4>Perfil personal</h4>
                </div>
            </div>
            <form onSubmit={handleSubmit(saveProfileForm)}>
                <CxWrap>
                    <CxCard>Modificar la foto de perfil</CxCard>
                    <Line />
                    <CxCard>
                        <div className="flex flex-col items-center">
                            <div className="mb-4">
                                <img
                                    src={previewImage}
                                    alt="Preview"
                                    className="mt-4 max-w-full h-32 w-32 rounded-full"
                                />
                            </div>
                            <div className="rounded-full border border-gray-100 bg-white p-3 shadow-md w-1/3">
                                <label htmlFor="upload" className="flex flex-col items-center gap-2 cursor-pointer">
                                    <span className="text-gray-600 font-medium">Importar foto de perfil</span>
                                </label>
                                <input
                                    id="upload"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="hidden"
                                />
                            </div>
                            <ErrMsg message={errors.personalImage?.message} />
                        </div>
                    </CxCard>
                </CxWrap>
                <CxWrap>
                    <CxCard>Datos generales</CxCard>
                    <Line />
                    <CxCard>
                        <div className="grid grid-cols-6 gap-6">
                            <HalfRow>
                                <FormInputField
                                    identifier="firstName"
                                    label="Nombres"
                                    iconComponent={<UsersIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
                                    type="string"
                                    registration={register('firstName', { required: 'Este campo es obligatorio' })}
                                    errorMessage={errors.firstName?.message}
                                />
                            </HalfRow>
                            <HalfRow>
                                <FormInputField
                                    identifier="lastName"
                                    label="Apellidos"
                                    iconComponent={<UsersIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
                                    type="string"
                                    registration={register('lastName', { required: 'Este campo es obligatorio' })}
                                    errorMessage={errors.lastName?.message}
                                />
                            </HalfRow>
                            <FullRow>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                                <Controller
                                    name="fullPhone"
                                    control={control}
                                    rules={{ 
                                        validate: () => {
                                            const phone = getValues('phone') || '';
                                            const digits = phone.replace(/\D/g, '');
                                            
                                            if (!digits) {
                                                return 'Este campo es obligatorio';
                                            }
                                            
                                            if (digits.length < 8) {
                                                return 'Número de teléfono inválido';
                                            }
                                            
                                            return true;
                                        }
                                    }}
                                    render={({ field: { onChange, value, ref } }) => {
                                        // Get countryCode and phone from form values
                                        const countryCode = getValues('countryCode') || '';
                                        const phone = getValues('phone') || '';
                                        
                                        // Construct merged value: prefer value from field, otherwise combine countryCode + phone
                                        let mergedValue = value;
                                        if (!mergedValue || mergedValue.trim() === '') {
                                            if (countryCode || phone) {
                                                // Remove + from countryCode if present and combine
                                                const cleanCode = countryCode.replace(/^\+/, '');
                                                mergedValue = `${cleanCode}${phone}`;
                                            }
                                        }

                                        return (
                                            <PhoneInput
                                                value={mergedValue || ''}
                                                onChange={(val, country) => {
                                                    const codeVal = `+${country.dialCode}`;
                                                    let phoneVal = val;
                                                    
                                                    if (val.startsWith(`+${country.dialCode}`)) {
                                                        phoneVal = val.substring(`+${country.dialCode}`.length);
                                                    } else if (val.startsWith(country.dialCode)) {
                                                        phoneVal = val.substring(country.dialCode.length);
                                                    }
                                                    
                                                    phoneVal = phoneVal.replace(/^\+/, '').trim();

                                                    setValue('countryCode', codeVal);
                                                    setValue('phone', phoneVal);
                                                    
                                                    onChange(val);
                                                    
                                                    setValue('fullPhone', val, { shouldValidate: true }); 
                                                }}
                                                onBlur={() => {
                                                    trigger('fullPhone');
                                                }}
                                                enableSearch
                                                disableDropdown={false}
                                                disableCountryCode={false}
                                                disableAreaCodes={true}
                                                countryCodeEditable={false}
                                                placeholder="Ingrese su número"
                                                containerClass="rounded-full w-full custom-phone-container1"
                                                inputClass="w-full h-10 pl-14 pr-4 rounded-full border border-gray-300 focus:outline-none focus:ring-0 focus:border-gray-300 active:outline-none active:ring-0 active:border-gray-300"
                                                buttonClass="rounded-l-full border-r border-gray-300 bg-white focus:outline-none focus:ring-0 focus:border-gray-300 active:outline-none active:ring-0 active:border-gray-300"
                                                specialLabel=""
                                                inputProps={{ ref }}
                                            />
                                        );
                                    }}
                                />
                                {errors.fullPhone && (
                                    <p className="text-sm text-red-700 font-light mt-2">{errors.fullPhone.message}</p>
                                )}
                            </FullRow>




                            <HalfRow>
                                <FormInputField
                                    identifier="address"
                                    label="Domicilio"
                                    type="string"
                                    registration={register('address')}
                                />
                            </HalfRow>
                            {userType == 'patient' ? (
                                <div className="col-span-6 sm:col-span-3">
                                    <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                                        Seleccione País de residencia
                                    </label>
                                    <select
                                        identifier="country"
                                        id="country"
                                        {...register('country', { required: 'Este campo es obligatorio' })}
                                        value={watch('country') || ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setSelectedCountry(value);
                                            setValue('country', value);
                                            setValue('state', '');
                                        }}
                                        className="mt-1 block w-full bg-white border border-gray-300 rounded-full shadow-sm px-3 py-2 focus:outline-none focus:ring-dl-primary-500 focus:border-dl-primary-500 sm:text-sm"
                                    >
                                        <option value="">-- Select Country --</option>
                                        {countryOptions.map((country) => (
                                            <option key={country.value} value={country.value}>
                                                {country.label}
                                            </option>
                                        ))}
                                    </select>

                                    {errors.country && (
                                        <p className="text-sm text-red-700 font-light mt-2">{errors.country.message}</p>
                                    )}
                                </div>
                            ) : null}
                            {userType === 'patient' && selectedCountry && (
                                <div className="col-span-6 sm:col-span-3">
                                    <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                                        Seleccione residencia
                                    </label>
                                    <select
                                        identifier="state"
                                        id="state"
                                        {...register('state', { required: 'Este campo es obligatorio' })}
                                        value={watch('state') || ''}
                                        className="mt-1 block w-full bg-white border border-gray-300 rounded-full shadow-sm px-3 py-2 focus:outline-none focus:ring-dl-primary-500 focus:border-dl-primary-500 sm:text-sm"
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setValue('state', value);
                                        }}
                                    >
                                        <option value="">-- Select State/Residence --</option>
                                        {stateOptions.map((state) => (
                                            <option key={state.value} value={state.value}>
                                                {state.label}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.state && (
                                        <p className="text-sm text-red-700 font-light mt-2">{errors.state.message}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </CxCard>
                </CxWrap>

                <CxWrap>
                    <CxCard>Documentación</CxCard>
                    <Line />
                    <CxCard>
                        <div className="grid grid-cols-6 gap-6">
                            {userType === 'doctor' ? (
                                <FullRow>
                                    <Controller
                                        control={control}
                                        name="nationality"
                                        rules={{ 
                                            validate: (value) => {
                                                if (!value || value.id === 'default') {
                                                    return 'Este campo es obligatorio';
                                                }
                                                return true;
                                            }
                                        }}
                                        render={({ field }) => (
                                            <FormComboBox
                                                label="Nacionalidad"
                                                data={formattedCountryData}
                                                control={control}
                                                value={field.value}
                                                onChange={(value) => field.onChange(value)}
                                            />
                                        )}
                                    />
                                    {errors.nationality && (
                                        <p className="text-red-700 font-light mt-2 text-sm">{errors.nationality.message}</p>
                                    )}
                                </FullRow>
                            ) : null}
                            <HalfRow>
                                <FormInputField
                                    identifier="personalDocument"
                                    label="Documento de identificación"
                                    iconComponent={
                                        <IdentificationIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                    }
                                    type="string"
                                    registration={register('personalDocument', { required: 'Este campo es obligatorio' })}
                                    errorMessage={errors.personalDocument?.message}
                                />
                            </HalfRow>
                            <HalfRow>
                                <div className="w-full">
                                    <label htmlFor="documentType" className="block text-sm font-medium text-gray-700">
                                        Tipo de documento
                                    </label>
                                    <select
                                        id="documentType"
                                        {...register('documentType', { required: 'Este campo es obligatorio' })}
                                        className="mt-1 block w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                                    >
                                        <option value="">Seleccione un tipo</option>
                                        {documentTypes.map((docType) => (
                                            <option key={docType.documentName} value={docType.documentName}>
                                                {docType.documentName}
                                            </option>
                                        ))}
                                    </select>
                                    {errors?.documentType && (
                                        <p className="text-red-700 font-light mt-2 text-sm">{errors.documentType.message}</p>
                                    )}
                                </div>
                            </HalfRow>

                            <FullRow>
                                <label htmlFor="birthDate" className="block text-sm font-medium text-gray-700">
                                    Fecha de nacimiento
                                </label>
                                <div className="relative mt-1 rounded-md shadow-sm">
                                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"></div>
                                    <Controller
                                        control={control}
                                        name="birthDate"
                                        rules={{ required: 'Este campo es obligatorio' }}
                                        render={({ field }) => {
                                            const value = field?.value;

                                            // Safely parse "dd/MM/yyyy" to Date object
                                            const selectedDate = value
                                                ? (() => {
                                                    const [day, month, year] = value.split('/');
                                                    const date = new Date(`${year}-${month}-${day}`);
                                                    return isNaN(date.getTime()) ? null : date;
                                                })()
                                                : null;

                                            return (
                                                <DatePicker
                                                    placeholderText="Selecciona la fecha"
                                                    className="pl-2 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                                    selected={selectedDate}
                                                    onChange={(date) => {
                                                        const formatted = date
                                                            ? `${String(date.getDate()).padStart(2, '0')}/${String(
                                                                date.getMonth() + 1
                                                            ).padStart(2, '0')}/${date.getFullYear()}`
                                                            : '';
                                                        field.onChange(formatted);
                                                    }}
                                                    dateFormat="dd/MM/yyyy"
                                                    maxDate={new Date()}
                                                    showYearDropdown
                                                    scrollableYearDropdown
                                                    yearDropdownItemNumber={100}
                                                />
                                            );
                                        }}
                                    />
                                    {errors.birthDate && (
                                        <p className="text-red-700 font-light mt-2 text-sm">{errors.birthDate.message}</p>
                                    )}
                                </div>
                            </FullRow>
                        </div>
                    </CxCard>
                </CxWrap>
                {userType === 'patient' ? (
                    <CxWrap>
                        <CxCard>Información complementaria</CxCard>
                        <Line />
                        <CxCard>
                            <div className="grid grid-cols-6 gap-6">
                                <HalfRow>
                                    <FormInputField
                                        identifier="gender"
                                        label="Sexo"
                                        type="string"
                                        registration={register('gender')}
                                    />
                                </HalfRow>
                                <div className="col-span-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Enfermedades (máx. 5)
                                    </label>
                                    <div className="space-y-4">
                                        {diseases.map((disease, index) => (
                                            <div key={index} className="mb-3">
                                                <input
                                                    type="text"
                                                    value={disease.name}
                                                    onChange={(e) => handleDiseaseChange(e, index)}
                                                    placeholder="Nombre de la enfermedad"
                                                    className="w-full border border-gray-300 rounded-md p-2"
                                                />
                                                <ErrMsg message={diseaseErrors[index]} />
                                                {index > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeDisease(index)}
                                                        className="text-red-600 text-sm mt-1"
                                                    >
                                                        Eliminar
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        {diseases.length < 5 && (
                                            <button
                                                type="button"
                                                onClick={addDisease}
                                                className="text-blue-600 text-sm mt-2"
                                            >
                                                + Añadir otra enfermedad
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <HalfRow>
                                    <FormInputField
                                        identifier="blood_type"
                                        label="Tipo de sangre"
                                        type="string"
                                        registration={register('blood_type')}
                                    />
                                </HalfRow>
                                <HalfRow>
                                    <FormInputField
                                        identifier="weight"
                                        label="Peso"
                                        type="string"
                                        registration={register('weight')}
                                    />
                                </HalfRow>

                                <HalfRow>
                                    <FormInputField
                                        identifier="height"
                                        label="Estatura"
                                        type="string"
                                        registration={register('height')}
                                    />
                                </HalfRow>
                            </div>
                        </CxCard>
                    </CxWrap>
                ) : null}

                <CxWrap>
                    <CxCard>Cuenta</CxCard>
                    <Line />
                    <CxCard>
                        <div className="grid grid-cols-6 gap-6">
                            <FullRow>
                                <FormInputField
                                    identifier="email"
                                    label="Correo electrónico"
                                    iconComponent={<MailIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
                                    type="string"
                                    registration={register('email', { required: 'Este campo es obligatorio' })}
                                    errorMessage={errors.email?.message}
                                    disabled
                                />
                            </FullRow>
                        </div>
                    </CxCard>
                </CxWrap>

                <div className="px-4 py-3 text-right sm:px-6 mt-4">
                    <button
                        type="submit"
                        className="bg-dl-primary-900 border border-transparent rounded-full shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-dl-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dl-primary-700"
                    >
                        Actualizar perfil personal
                    </button>
                </div>
            </form>

            <form onSubmit={handlePasswordSubmit(onUpdatePassword)}>
                <CxWrap>
                    <CxCard>Actualizar contraseña</CxCard>
                    <Line />
                    <CxCard>
                        <div className="grid grid-cols-6 gap-6">
                            <FullRow>
                                <FormInputField
                                    identifier="oldPassowrd"
                                    label="Contraseña actual"
                                    iconComponent={<KeyIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
                                    type="password"
                                    registration={registerPassword('oldPassword', { required: true })}
                                />
                                {passwordErrors.oldPassword?.type === 'required' ? (
                                    <p className="text-red-700 font-light">Inserte contraseña actual</p>
                                ) : null}
                            </FullRow>
                            <FullRow>
                                <FormInputField
                                    identifier="newPassword"
                                    label="Nueva contraseña"
                                    iconComponent={<KeyIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
                                    type="password"
                                    registration={registerPassword('newPassword', { required: true })}
                                />
                                {passwordErrors.newPassword?.type === 'required' ? (
                                    <p className="text-red-700 font-light">Inserte nueva contraseña</p>
                                ) : null}
                            </FullRow>
                            <FullRow>
                                <FormInputField
                                    identifier="newPasswordConfirm"
                                    label="Confirmar nueva contraseña"
                                    iconComponent={<KeyIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
                                    type="password"
                                    registration={registerPassword('newPasswordConf', {
                                        required: true,
                                        validate: validatePasswordConfirmation
                                    })}
                                />
                                {passwordErrors.newPasswordConf?.type === 'required' ? (
                                    <p className="text-red-700 font-light">Confirme contraseña</p>
                                ) : null}
                                {passwordErrors.newPasswordConf?.type === 'validate' ? (
                                    <p className="text-red-700 font-light">Las contraseñas deben coincidir</p>
                                ) : null}
                            </FullRow>
                        </div>
                    </CxCard>
                </CxWrap>
                <div className="px-4 py-3 text-right sm:px-6 mt-4">
                    <button
                        type="submit"
                        className="bg-dl-primary-900 border border-transparent rounded-full shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-dl-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dl-primary-700"
                    >
                        Actualizar contraseña
                    </button>
                </div>
            </form>
            {showError && (
                <CustomRedAlert open={showError} title="Ocurrió un error." bodyText={errorMsg} onClose={closeError} />
            )}

            {showSuccess && (
                <CustomSuccessModal
                    title={successMsg}
                    buttonText="OK"
                    buttonAction={closeSuccess}
                    open={showSuccess}
                    onClose={closeSuccess}
                />
            )}
        </>
    );
};

export default PersonalProfileForm;
